import { Spacing4, Spacing8 } from 'autogen/design-tokens/tokens';
import { _GetSelectedProduct, type BasketLineViewModel } from 'autogen/swagger/all';
import Flex from 'components/Layout/components/Flex';
import Card from 'components/generic/card';
import ProductImage from 'components/generic/productImage';
import styled from 'styled-components';
import { Typography } from 'components/generic/typography';
import { hideOnDesktop, hideOnMobile } from 'components/pages/Checkout/components/Delivery/shared/styles';
import BasketDiscountInfo from 'components/checkout/components/BasketOverview/components/DiscountInfo/BasketDiscountInfo';
import { OTCSplash } from 'components/product/components/OTCNotification/OTCNotification';
import StockStatus from 'components/expecteddelivery/handle';
import SubscriptionInformation from 'components/basket/BasketItem/components/Subscription/components/SubscriptionInformation';
import HighlightedText from 'components/generic/HighlightedText';
import { memo, useRef, useState } from 'react';
import Price from './components/Price';
import LinkedItemPrices from './components/Price/components/LinkedItemPrices';
import Actions from './components/Actions/Actions';
import BasketMatasPlusItem from './BasketMatasPlusItem';
import BasketDonationItem from './BasketDonationItem';
import { Grid, PriceContainer, ProductActions } from './shared/styles';
import SubscriptionFrequencyInfo from './components/Subscription/components/SubscriptionFrequencyInfo';
import PointDiscountLine from './components/PointDiscountLine';
import { useRecoilValue } from 'recoil';
import { requestDataState } from 'recoilState/atoms/requestDataState';
import { ProductImageContainerLink } from 'components/generic/productImage/shared/styles';
import { ProductContext, createProductStore } from 'components/pages/PDP/shared/productStore';
import type { ICard } from 'components/generic/card/shared/types';
import List from 'components/List';
import { useTranslation } from 'react-i18next';
import { format } from 'modules/helpers/strings';
import { ProductLinkLabel } from 'autogen/translation-keys/trans-website-basket';
import Icon from 'components/Icon';
import { ClickAndCollectStorePrefix } from 'autogen/translation-keys/trans-website-basket-clickandcollect';
import InlineButton from 'components/generic/InlineButton';
import { useFindInStoreQuickView } from 'components/storeList/shared/hooks';
import { use_GetSelectedProductQuery } from 'autogen/swagger/all/hooks';

/* #region styles */
const StyledCard = styled(Card)`
    border-radius: 0;
`;

const StyledOTCSplash = styled(OTCSplash)`
    position: absolute;
    right: 0;
    bottom: ${Spacing4};
`;

const ClickAndCollectInfo = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.Spacing8};
`;

const StyledIcon = styled(Icon)`
    fill: ${({ theme }) => theme.ColorBackgroundSuccess};
`;
/* #endregion */

type BasketItemProps = {
    model: BasketLineViewModel;
} & ICard;

const BasketItem = ({ model, ...restProps }: { model: BasketLineViewModel }) => {
    const {
        productId,
        productLinkAction,
        image,
        productName,
        discountInfo,
        linkedItems,
        otcWarning,
        stockStatus,
        subscriptionInfo,
        matasPlusInfo,
        donationInfo,
        booking,
        clickAndCollectStore
    } = model;
    const { t } = useTranslation();
    const { pointDiscount } = discountInfo;
    const {
        featureToggles: { pointAsDiscountEnabled }
    } = useRecoilValue(requestDataState);
    const [, toggle] = useFindInStoreQuickView();

    const productStore = useRef(createProductStore({ productId, isSubscription: !!subscriptionInfo })).current;

    const { refetch: getSelectedProduct } = use_GetSelectedProductQuery(
        {
            productId
        },
        {
            onSuccess(data) {
                toggle({
                    isOpen: true,
                    productId,
                    productInfo: data,
                    showClickAndCollectToggle: true,
                    clickAndCollectOnly: true,
                    isClickAndCollect: true,
                    showSelectParcelShop: true,
                    isBasket: true
                });
            },
            enabled: false
        }
    );

    if (matasPlusInfo) return <BasketMatasPlusItem model={model} />;
    if (donationInfo) return <BasketDonationItem model={model} />;

    let name = productName;
    let imageShown = image;

    if (booking) {
        name = booking.name;
        imageShown = { ...image, url: booking.image?.fallBackUrl ?? '' };
    }

    function openFindInStoreQuickView() {
        getSelectedProduct();
    }

    return (
        <ProductContext.Provider value={productStore}>
            <StyledCard size={null} {...restProps}>
                <Card.Content>
                    <Grid>
                        <ProductImageContainerLink
                            action={productLinkAction}
                            aria-label={format(t(ProductLinkLabel))(name)}
                        >
                            <ProductImage
                                width={104}
                                image={{ ...imageShown, 'aria-label': name }}
                                transformType="SquareScale"
                            />
                            {otcWarning && <StyledOTCSplash model={otcWarning} size="sm" />}
                        </ProductImageContainerLink>

                        {/* mobile price */}
                        <Flex.Column justifyContent="space-between" css={hideOnDesktop}>
                            <Flex.Column gap={Spacing8}>
                                {discountInfo && <BasketDiscountInfo model={discountInfo} />}
                                <Typography.Body as="span">{name}</Typography.Body>
                            </Flex.Column>

                            <PriceContainer gap={Spacing8} alignItems="end">
                                <Price model={model} />
                                <LinkedItemPrices model={linkedItems} />
                            </PriceContainer>
                        </Flex.Column>

                        <ProductActions justifyContent="space-between">
                            {/* desktop price */}
                            <Flex.Row justifyContent="space-between" css={hideOnMobile}>
                                <Flex.Column gap={Spacing8}>
                                    <Typography.Body as="span">{name}</Typography.Body>
                                    {stockStatus && (
                                        <List>
                                            <StockStatus model={stockStatus} css={hideOnDesktop} />
                                        </List>
                                    )}
                                    {clickAndCollectStore && (
                                        <ClickAndCollectInfo>
                                            <StyledIcon type="Checkmark" size={16} />
                                            <div>
                                                <Typography.Body as="span" strong>
                                                    {t(ClickAndCollectStorePrefix)}
                                                </Typography.Body>{' '}
                                                <InlineButton onClick={openFindInStoreQuickView}>
                                                    {clickAndCollectStore}
                                                </InlineButton>
                                            </div>
                                        </ClickAndCollectInfo>
                                    )}
                                    {subscriptionInfo && (
                                        <HighlightedText withBorder={false}>
                                            <Flex.Column gap={Spacing4}>
                                                <SubscriptionInformation
                                                    model={subscriptionInfo}
                                                    productId={productId}
                                                />
                                                <SubscriptionFrequencyInfo model={subscriptionInfo} />
                                            </Flex.Column>
                                        </HighlightedText>
                                    )}
                                    {pointAsDiscountEnabled && pointDiscount.text && (
                                        <PointDiscountLine {...pointDiscount} />
                                    )}
                                    {booking && (
                                        <HighlightedText withBorder={false}>
                                            {booking.reservedUntilText}
                                        </HighlightedText>
                                    )}
                                </Flex.Column>

                                <PriceContainer gap={Spacing8} alignItems="end">
                                    {discountInfo && <BasketDiscountInfo model={discountInfo} />}
                                    <Price model={model} />
                                    <LinkedItemPrices model={linkedItems} />
                                </PriceContainer>
                            </Flex.Row>

                            <Actions model={model} />

                            {/* Stock status, subscription and points discound mobile view */}
                            <Flex.Column css={hideOnDesktop}>
                                <Flex.Column gap={Spacing8}>
                                    {stockStatus && <StockStatus model={stockStatus} css={hideOnDesktop} />}
                                    {clickAndCollectStore && (
                                        <ClickAndCollectInfo>
                                            <StyledIcon type="Checkmark" size={16} />
                                            <div>
                                                <Typography.Body as="span" strong>
                                                    {t(ClickAndCollectStorePrefix)}
                                                </Typography.Body>{' '}
                                                <InlineButton onClick={openFindInStoreQuickView}>
                                                    {clickAndCollectStore}
                                                </InlineButton>
                                            </div>
                                        </ClickAndCollectInfo>
                                    )}
                                </Flex.Column>
                                {subscriptionInfo && (
                                    <HighlightedText withBorder={false}>
                                        <Flex.Column gap={Spacing4}>
                                            <SubscriptionInformation model={subscriptionInfo} productId={productId} />
                                            <SubscriptionFrequencyInfo model={subscriptionInfo} />
                                        </Flex.Column>
                                    </HighlightedText>
                                )}
                                {pointAsDiscountEnabled && pointDiscount.text && (
                                    <PointDiscountLine {...pointDiscount} />
                                )}
                            </Flex.Column>
                        </ProductActions>
                    </Grid>
                </Card.Content>
            </StyledCard>
        </ProductContext.Provider>
    );
};

export default memo(BasketItem);
